import type {
  InjectionProtocol,
  ProtocolSite,
} from "../injection-protocol/types";

/**
 * What her camera is for, on this procedure.
 *
 * Two answers, and the difference is not cosmetic.
 *
 * **`guide`** is what the coach was built for: the injection area outlined on
 * her own body, from her own shoulders and hips. It exists because a drawing of
 * somebody's abdomen laid over hers has to choose a shape and whichever it
 * chooses is wrong for most people.
 *
 * **`mirror`** is the camera doing the older, simpler thing: letting her see a
 * part of herself she cannot see. An intramuscular dose goes into the upper
 * outer quadrant of her buttock. She cannot look at it, the front camera can,
 * and there is nothing to outline there — the pose geometry models a torso from
 * shoulders to hips, and the site is behind her.
 *
 * ### Why this is derived and not declared
 *
 * It could have been a field on the protocol. It is computed from the route
 * instead, because the thing that decides it is anatomy rather than product
 * intent: any procedure whose site is in front of her and between her shoulders
 * and hips can be guided, and every other one cannot. A field would let a
 * protocol claim a guide the geometry cannot place — which is exactly the
 * failure the outline was rewritten to remove.
 *
 * ### What a mirror must never pretend to be
 *
 * No silhouette, no framing gate, no outline, and no message about being seen
 * or not seen: there is no detector reading anything, so any of those would be
 * the overlay asserting an observation nobody made. What stays is the picture,
 * the control that turns it off, and the promise that nothing is recorded —
 * which is as true here as anywhere else in this package.
 */
export type CameraRole = "guide" | "mirror";

export function cameraRoleFor(
  protocol: InjectionProtocol,
  site?: ProtocolSite["id"] | null,
): CameraRole {
  // A site she chose decides before anything else, and what it decides is
  // whether anything can be placed there.
  //
  // The abdomen and the thigh both can: the pose layer reads shoulders, hips
  // and knees, so it draws an area between shoulders and hips for one and a
  // band between a hip and a knee for the other. A buttock cannot — it is
  // behind her, no landmark reaches it, and an outline drawn there would be the
  // overlay asserting a placement nobody computed.
  if (site && site !== "abdomen" && site !== "thigh") return "mirror";

  // Subcutaneous in this catalogue means the lower abdomen, which is the one
  // region the pose layer can place an outline on. Everything else — starting
  // with the gluteal quadrant urinary hCG names — is a mirror.
  return protocol.route === "subcutaneous" ? "guide" : "mirror";
}
